import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import { DepGraph, GRAPH_LARGE_THRESHOLD, type LayoutMode } from '@/features/graph/DepGraph'
import { ModuleDetailPanel } from '@/features/graph/components/ModuleDetailPanel'
import { GraphFilterRail } from '@/features/graph/components/GraphFilterRail'
import { GraphRootPicker } from '@/features/graph/components/GraphRootPicker'
import { GraphStatusBar } from '@/features/graph/components/GraphStatusBar'
import { GraphLockFileHelper } from '@/features/graph/components/GraphLockFileHelper'
import { useOrgGraph, useOrgs } from '@/lib/hooks'
import type { GraphKind, GraphNode } from '@/lib/apiTypes'
import {
  ALL_KINDS, ALL_RISKS, computeView, defaultDirection, filterCounts,
  type Depth, type Direction, type FilterState, type OwnershipFilter, type RiskFilter,
} from '@/features/graph/lib/graphView'
import s from '@/features/graph/graph.module.css'

/**
 * The org's dependency graph.
 *
 * Every piece of view state lives in the URL, so a narrowed view is a shareable link:
 *   ?focus=&dir=&depth=&kinds=&risk=&own=&prov=&hide=&q=&layout=
 *
 * The page owns narrowing (via lib/graphView) and hands DepGraph an already-reduced set. That
 * is what lets clicking a node REMOVE everything outside its lineage rather than dimming it,
 * and it keeps filtering and isolation composing as one intersection instead of two competing
 * emphasis channels.
 */

const CSV = {
  parse: <T extends string>(raw: string | null, valid: readonly T[]): Set<T> => {
    if (!raw) return new Set<T>()
    const ok = new Set<string>(valid)
    return new Set(raw.split(',').filter((v) => ok.has(v)) as T[])
  },
  write: (set: Set<string>): string | null => (set.size === 0 ? null : [...set].join(',')),
}

function parseDepth(raw: string | null): Depth {
  if (raw === '1') return 1
  if (raw === '2') return 2
  return 'all'
}

export function GraphPage() {
  const { orgSlug } = useParams()
  const orgs = useOrgs()
  const navigate = useNavigate()
  const selectedOrg = orgs.data?.orgs.find((o) => (o.slug ?? o.id) === orgSlug)
  const [searchParams, setSearchParams] = useSearchParams()

  const graph = useOrgGraph(orgSlug ?? null)
  const [railCollapsed, setRailCollapsed] = useState(false)
  const [selected, setSelected] = useState<GraphNode | null>(null)
  const [lockFileTarget, setLockFileTarget] = useState<GraphNode | null>(null)
  const [largeNudgeDismissed, setLargeNudgeDismissed] = useState(false)

  // ── URL-backed view state ───────────────────────────────────────────────────
  // The URL is the single source of truth; nothing is mirrored into useState, so the two can
  // never disagree. Every write is a functional update with replace:true so narrowing the view
  // does not fill the back stack.

  const focusId = searchParams.get('focus')
  const layoutMode: LayoutMode = searchParams.get('layout') === 'organic' ? 'organic' : 'layered'
  const depth = parseDepth(searchParams.get('depth'))
  const rawDir = searchParams.get('dir')
  const search = searchParams.get('q') ?? ''

  const filters: FilterState = useMemo(() => ({
    kinds: CSV.parse<GraphKind>(searchParams.get('kinds'), ALL_KINDS),
    risk: CSV.parse<RiskFilter>(searchParams.get('risk'), ALL_RISKS),
    ownership: CSV.parse<OwnershipFilter>(searchParams.get('own'), ['internal', 'external']),
    providers: new Set((searchParams.get('prov') ?? '').split(',').filter(Boolean)),
    vendors: new Set((searchParams.get('vendor') ?? '').split(',').filter(Boolean)),
    hideOrphans: searchParams.get('orphans') === 'hide',
    search,
  }), [searchParams, search])

  const hiddenIds = useMemo(
    () => new Set((searchParams.get('hide') ?? '').split(',').filter(Boolean)),
    [searchParams],
  )

  const setParams = useCallback((mutate: (p: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      mutate(p)
      return p
    }, { replace: true })
  }, [setSearchParams])

  /** Deleting rather than emptying keeps the URL honest about what is actually set. */
  const setParam = useCallback((key: string, value: string | null) => {
    setParams((p) => { if (value === null) p.delete(key); else p.set(key, value) })
  }, [setParams])

  const nodes = graph.data?.nodes ?? []
  const edges = graph.data?.edges ?? []
  const stats = graph.data?.stats

  const focused = useMemo(
    () => (focusId ? nodes.find((n) => n.id === focusId) ?? null : null),
    [focusId, nodes],
  )

  // Direction defaults from the focused node's KIND, because the interesting direction differs:
  // a state file only has downstream, a provider only has upstream, a module has both.
  const direction: Direction = useMemo(() => {
    if (rawDir === 'up' || rawDir === 'down' || rawDir === 'both') return rawDir
    return focused ? defaultDirection(focused.kind) : 'both'
  }, [rawDir, focused])

  const view = useMemo(() => computeView({
    nodes, edges, filters, focusId, direction, depth, hiddenIds,
  }), [nodes, edges, filters, focusId, direction, depth, hiddenIds])

  const counts = useMemo(() => filterCounts(nodes, edges, filters), [nodes, edges, filters])

  const activeFilterCount = filters.kinds.size + filters.risk.size + filters.ownership.size
    + filters.providers.size + filters.vendors.size + (filters.hideOrphans ? 1 : 0)

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Click = isolate this node's lineage AND inspect it. One gesture, both outcomes. */
  const activateNode = useCallback((node: GraphNode) => {
    setSelected(node)
    setParams((p) => {
      p.set('focus', node.id)
      // Clear any explicit direction so the new node's kind picks its own default.
      p.delete('dir')
    })
    if (node.kind === 'state' && node.has_lock_file === false) setLockFileTarget(node)
  }, [setParams])

  const clearFocus = useCallback(() => {
    setParams((p) => { p.delete('focus'); p.delete('dir'); p.delete('depth') })
  }, [setParams])

  const clearFilters = useCallback(() => {
    setParams((p) => {
      for (const k of ['kinds', 'risk', 'own', 'prov', 'vendor', 'orphans']) p.delete(k)
    })
  }, [setParams])

  const resetAll = useCallback(() => {
    setParams((p) => {
      for (const k of ['focus', 'dir', 'depth', 'kinds', 'risk', 'own', 'prov', 'vendor', 'orphans', 'hide', 'q']) p.delete(k)
    })
    setSelected(null)
  }, [setParams])

  // Escape is the keyboard twin of Reset. Skipped while a dialog or an input has focus, so it
  // does not fight a modal's own Escape handling or interrupt typing.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      const el = document.activeElement
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return
      if (document.querySelector('dialog[open]')) return
      resetAll()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [resetAll])

  const toggleIn = useCallback(<T extends string>(key: string, valid: readonly T[], value: T) => {
    setParams((p) => {
      const next = CSV.parse<T>(p.get(key), valid)
      if (next.has(value)) next.delete(value); else next.add(value)
      const w = CSV.write(next as Set<string>)
      if (w === null) p.delete(key); else p.set(key, w)
    })
  }, [setParams])

  /** Shared by the two free-text facets (provider type, vendor), whose values are not a fixed enum. */
  const toggleFreeText = useCallback((key: string, value: string) => {
    setParams((p) => {
      const next = new Set((p.get(key) ?? '').split(',').filter(Boolean))
      if (next.has(value)) next.delete(value); else next.add(value)
      if (next.size === 0) p.delete(key); else p.set(key, [...next].join(','))
    })
  }, [setParams])

  const hideNode = useCallback((id: string) => {
    setParams((p) => {
      const next = new Set((p.get('hide') ?? '').split(',').filter(Boolean))
      next.add(id)
      p.set('hide', [...next].join(','))
    })
    setSelected((cur) => (cur?.id === id ? null : cur))
  }, [setParams])

  // Keep the inspected node in step with refetched data, so its status is never stale after a
  // deprecate or a block.
  useEffect(() => {
    setSelected((cur) => (cur ? nodes.find((n) => n.id === cur.id) ?? null : null))
  }, [nodes])

  if (!orgs.loading && !selectedOrg) {
    return (
      <Layout title={<>Graph <span className="badge badge-amber">Experimental</span></>}>
        <div className={s.noOrg}>No organizations found</div>
      </Layout>
    )
  }

  const unscanned = stats ? stats.total - stats.scanned - stats.unscanned_no_versions : 0
  const showCoverage = !!stats && (stats.scanned < stats.total || stats.workspaces_without_lock_file > 0)

  return (
    <Layout title={<>Graph <span className="badge badge-amber">Experimental</span></>} fillHeight>
      {/* Toolbar: root + direction on the left, search and layout on the right. */}
      <div className={s.toolbar}>
        <GraphRootPicker
          nodes={nodes}
          focusId={focusId}
          direction={direction}
          onPick={activateNode}
          onClear={clearFocus}
          onDirection={(d) => setParam('dir', d)}
        />

        <div className={s.spacer} />

        <input
          className="input"
          style={{ fontSize: '0.76rem', padding: '5px 9px', width: 190 }}
          placeholder="Search the graph…"
          value={search}
          onChange={(e) => setParam('q', e.target.value || null)}
        />

        <div className={s.seg} role="group" aria-label="Layout">
          <button
            className={`${s.segBtn} ${layoutMode === 'layered' ? s.segBtnOn : ''}`}
            aria-pressed={layoutMode === 'layered'}
            title="Layered left-to-right by dependency depth. Deterministic, so the same graph looks the same every time."
            onClick={() => setParam('layout', null)}
          >
            Layered
          </button>
          <button
            className={`${s.segBtn} ${layoutMode === 'organic' ? s.segBtnOn : ''}`}
            aria-pressed={layoutMode === 'organic'}
            title="Force-directed: clusters find their own shape, and nodes are draggable."
            onClick={() => setParam('layout', 'organic')}
          >
            Organic
          </button>
        </div>
      </div>

      {/* Coverage, not decoration: it explains what the graph is NOT showing. */}
      {showCoverage && (
        <div className={s.coverage}>
          Showing {stats!.scanned} of {stats!.total} modules
          {stats!.unscanned_no_versions > 0 && ` · ${stats!.unscanned_no_versions} have no published version`}
          {unscanned > 0 && ` · ${unscanned} not scanned yet`}
          {stats!.workspaces_without_lock_file > 0 && (
            <> · <strong>{stats!.workspaces_without_lock_file} of {stats!.workspaces} workspaces have no lock file</strong>,
            so their provider pins are invisible</>
          )}
          {' · '}
          <Link to="/integrations/lock-files" className={s.coverageLink}>Lock file guide →</Link>
        </div>
      )}

      {view.nodes.length > GRAPH_LARGE_THRESHOLD && !largeNudgeDismissed && (
        <div className={s.nudge}>
          <span>
            {view.nodes.length} nodes is a lot to read at once — click a node to isolate its
            lineage, or narrow with the filters. Showing a static layout for performance.
          </span>
          <button className={s.nudgeDismiss} onClick={() => setLargeNudgeDismissed(true)}>✕ Dismiss</button>
        </div>
      )}

      {/* The frame owns the only border, and claims the leftover height. */}
      <div className={s.frame}>
        <GraphFilterRail
          filters={filters}
          counts={counts}
          depth={depth}
          onDepth={(d) => setParam('depth', d === 'all' ? null : String(d))}
          depthEnabled={!!focusId}
          hiddenCount={hiddenIds.size}
          onUnhideAll={() => setParam('hide', null)}
          onToggleKind={(k) => toggleIn('kinds', ALL_KINDS, k)}
          onToggleRisk={(r) => toggleIn('risk', ALL_RISKS, r)}
          onToggleOwnership={(o) => toggleIn('own', ['internal', 'external'] as const, o)}
          onToggleProvider={(t) => toggleFreeText('prov', t)}
          onToggleVendor={(v) => toggleFreeText('vendor', v)}
          onToggleOrphans={() => setParam('orphans', filters.hideOrphans ? null : 'hide')}
          collapsed={railCollapsed}
          onCollapse={setRailCollapsed}
        />

        <div className={s.canvasWrap}>
          <GraphStatusBar
            shown={view.nodes.length}
            total={view.totalNodes}
            focused={focused}
            direction={direction}
            depth={depth}
            activeFilterCount={activeFilterCount}
            hiddenCount={hiddenIds.size}
            searchTerm={search}
            onClearFocus={clearFocus}
            onClearFilters={clearFilters}
            onClearSearch={() => setParam('q', null)}
            onUnhideAll={() => setParam('hide', null)}
            onResetAll={resetAll}
            onOpenFilters={() => setRailCollapsed(false)}
          />
          <DepGraph
            data={{ nodes: view.nodes, edges: view.edges }}
            loading={graph.loading}
            error={graph.error}
            height="100%"
            currentOrg={selectedOrg?.slug}
            layoutMode={layoutMode}
            highlightIds={view.highlightIds}
            orphanIds={view.orphanIds}
            cycleIds={view.cycleIds}
            blastRadius={view.blastRadius}
            focusNodeId={focusId}
            onNodeActivate={activateNode}
            onHideNode={hideNode}
            onLockFileHelp={setLockFileTarget}
            onMutated={() => graph.refetch()}
          />
        </div>

        {selected && (
          <ModuleDetailPanel
            node={selected}
            allNodes={nodes}
            allEdges={edges}
            currentOrg={selectedOrg?.slug}
            blast={view.blastRadius.get(selected.id)}
            inCycle={view.cycleIds.has(selected.id)}
            onClose={() => setSelected(null)}
            onSelectNode={activateNode}
            onMutated={() => graph.refetch()}
            onLockFileHelp={setLockFileTarget}
          />
        )}
      </div>

      {lockFileTarget && orgSlug && (
        <GraphLockFileHelper
          node={lockFileTarget}
          onClose={() => setLockFileTarget(null)}
          onOpenWorkspace={(name) => navigate(`/org/${orgSlug}/state/${encodeURIComponent(name)}`)}
        />
      )}
    </Layout>
  )
}
